import React from "react";
import { Trans, useTranslation } from "react-i18next";
import AuthService from "src/services/authService";
import { useDispatch } from "src/store";
import { Tooltip } from "src/ui/shared/Tooltip";

import { ConfirmDialog } from "@dashboard/devices/components/shared";
import { updateGroupMembers } from "@dashboard/devices/store";
import { Group, GroupMember } from "@dashboard/devices/types";
import { useGroupsByMemberEmail } from "@dashboard/groups/store/helpers";
import { DialogContentText } from "@mui/material";
import { memberSubject, PermissionService } from "@services/permissionService/permissionService";
import { DangerOutlinedButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { useCurrentWorkspace } from "../../api/helpers";

function getRoleToRemove(memberEmail: string, groupId: number | null, groups: Group[]) {
    if (groupId === null) {
        return null;
    }

    const group = groups.find((item) => item.id === groupId);
    if (!group) {
        return null;
    }

    const member = group.members.find((item) => item.email === memberEmail);
    if (!member) {
        return null;
    }

    return member.role;
}

type Props = {
    group: Group;
    member: GroupMember;
};
export const RemoveGroupMemberButton = (props: Props) => {
    const [loading, setLoading] = React.useState(false);
    const [dialogOpen, setDialogOpen] = React.useReducer((state) => !state, false);

    const { t } = useTranslation(["common", "pages", "modals", "sidepanels"]);
    const dispatch = useDispatch();

    const groups = useGroupsByMemberEmail(props.member.email);
    const roleToRemove = getRoleToRemove(props.member.email, props.group.id, groups);

    const { groupAbility } = PermissionService();
    const { data: workspace } = useCurrentWorkspace();
    const ability = groupAbility(props.group, workspace);
    const mayManageMember = ability.can("manage", "Member");
    const mayManageThisMember = ability.can("manage", memberSubject(props.member.role));
    const targetIsOwner = props.member.role === "Owner";

    const user = AuthService.getCurrentUser();
    const isUserRemovingHimself = user?.email === props.member.email;

    const onConfirmDelete = async () => {
        try {
            setLoading(true);
            await dispatch(updateGroupMembers({ id: props.group.id, removedMembers: [props.member.email] })).unwrap();
            showSuccessToast(t("pages:workspacePage.toast.memberRemovedSuccess"));
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getButtonProps = () => {
        if (isUserRemovingHimself) {
            return { title: t("pages:workspacePage.tooltip.removeFromGroup"), mayRemove: true };
        }
        if (targetIsOwner && !mayManageThisMember) {
            return { title: t("sidepanels:groupPanel.tooltip.ownerCannotBeDeleted"), mayRemove: false };
        }
        if (!mayManageMember) {
            return { title: t("pages:workspacePage.tooltip.cannotManageMembers"), mayRemove: false };
        }

        return { title: t("pages:workspacePage.tooltip.removeFromGroup"), mayRemove: true };
    };

    const { title, mayRemove } = getButtonProps();

    return (
        <React.Fragment>
            <Tooltip title={title} disableChildrenWrapper>
                <DangerOutlinedButton onClick={setDialogOpen} disabled={!mayRemove} loading={loading}>
                    <i className="fas fa-trash-alt" />
                </DangerOutlinedButton>
            </Tooltip>

            <ConfirmDialog
                title={t("modals:removeMember.title")}
                primaryActionText={t("common:button.remove")}
                open={dialogOpen}
                onConfirm={onConfirmDelete}
                onClose={setDialogOpen}
                dangerButton
            >
                <DialogContentText>
                    <Trans
                        ns="modals"
                        i18nKey="removeMember.descriptionGroup"
                        values={{
                            email: props.member.email,
                            role: roleToRemove,
                            name: props.group.name,
                        }}
                        components={{
                            bold: <span style={{ fontWeight: "bold" }} />,
                        }}
                    />
                </DialogContentText>
            </ConfirmDialog>
        </React.Fragment>
    );
};
